import React, { useState, useEffect, useMemo } from "react";
import { FaArrowLeft, FaArrowRight } from "react-icons/fa";
import { useLocation, useNavigate } from "react-router-dom";
// ⬇️ adapte le chemin si besoin
import { getSettings } from "./../../services/settingsServices";

/** 1) Chemin -> clé display_* */
const DISPLAY_BY_PATH_RAW = {
  // Utilisateur
  "/absences": "display_absences",
  "/calendrier": "display_calendrier",
  "/chat": "display_chat",
  "/boutique": "display_boutique",
  "/commandes-&-reglements": "display_commandes",
  "/prepa-physique": "display_prepa_physique",
  "/actualites": "display_news",
  "/galerie-photos": "display_galerie",
  "/lexique": "display_lexique",
  "/arbitrage-judo": "display_arbitrage",
  "/applications-judo": "display_applications",
  "/techniques-judo": "display_techniques",
  "/techniques-quizz": "display_techniquesquizz",
  "/vote-sondage-utilisateur": "display_vote",

  // Enseignant
  "/appel-presence": "display_appels",
  "/tableau-appel": "display_tableauappel",
  "/passage-ceinture": "display_passage",
  "/grade-cn": "display_gradecn",
  "/evenements": "display_evenements",
  "/prepa-physique-coaching": "display_prepa_physique_coaching",
  "/suivi-competitions": "display_suivicompetition",
  "/palmares": "display_palmares",
  "/objectifs": "display_objectifs",
  "/todolist": "display_todolist",
  "/techniques-enseignants": "display_techniquesenseignants",
  "/categories": "display_categories",

  // Admin
  "/articles-admin": "display_articlesadmin",
  "/depenses-recettes": "display_depensesrecettes",
  "/coachings": "display_coaching",
  "/heures-enseignement": "display_heures_enseignants",
  "/inventaire": "display_inventaire",
  "/pret-materiel": "display_pretmateriel",
  "/chat-page-admin": "display_chat_admin",
  "/message-admin": "display_messages_admin",
  "/vote-sondage-admin": "display_voteadmin",
  "/pages-personnalisees": "display_pagespersonnalisees",
};

/** 2) Normalisation de path */
const normalizePath = (p) =>
  (p || "")
    .toLowerCase()
    .replace(/\/$/, "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

/** 3) Mapping normalisé */
const DISPLAY_BY_PATH = Object.fromEntries(
  Object.entries(DISPLAY_BY_PATH_RAW).map(([path, key]) => [
    normalizePath(path),
    key,
  ])
);

/** 4) Unwrap settings (array / dataValues Sequelize) */
const unwrapSettings = (s) => {
  if (!s) return {};
  let out = s;
  if (Array.isArray(out)) out = out[0] || {};
  if (out && out.dataValues) out = out.dataValues;
  return out || {};
};

/** 5) True strict : accepte true / 1 / "1" / "true" / "on" */
const isTrue = (val) => {
  if (val === undefined || val === null) return false;
  if (typeof val === "boolean") return val;
  if (typeof val === "number") return val !== 0;
  if (typeof val === "string") {
    const v = val.trim().toLowerCase();
    return v === "true" || v === "1" || v === "yes" || v === "on";
  }
  return !!val;
};

/** 6) Normalise le rôle reçu (1/2/3 ou "user"/"enseignant"/"admin") */
const toRoleKey = (role) => {
  if (role === 2 || /^utilisateur|user$/i.test(String(role))) return "user";
  if (role === 3 || /^enseignant|teacher$/i.test(String(role)))
    return "enseignant";
  // 1 (admin) ou autre → admin par défaut
  return "admin";
};

const DashboardNavigation = ({ role }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [currentIndex, setCurrentIndex] = useState(-1);
  const [currentPaths, setCurrentPaths] = useState([]);
  const [store, setStore] = useState({}); // settings “déplié”
  const [loaded, setLoaded] = useState(false); // prêt à filtrer

  // charge settings en interne
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const data = await getSettings(); // peut renvoyer [] ou [{...}] ou {...}
        if (!mounted) return;
        setStore(unwrapSettings(data));
      } catch (e) {
        console.error("getSettings failed:", e);
        // en cas d’échec, store reste {}, donc seules les routes sans display seront visibles (comportement sûr)
      } finally {
        if (mounted) setLoaded(true);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const paths = {
    user: [
      "/tableau-de-bord",
      "/dossier-d-inscription",
      "/fiche-adherent",
      // "/absences",
      "/messages",
      "/chat",
      "/calendrier",
      "/boutique",
      "/commandes-&-reglements",
      "/actualites",
      "/galerie-photos",
      "/lexique",
      // "/wado-ryu",
      // "/historique",
      // "/kata",
      // "/arbitrage-karate",
      "/techniques-krav-maga",
      // "/vote-sondage-utilisateur",
    ],
    enseignant: [
      "/tableau-de-bord",
      "/dossier-d-inscription",
      "/fiche-adherent",
      // "/absences",
      "/messages",
      "/chat",
      "/boutique",
      "/commandes-&-reglements",
      "/actualites",
      "/galerie-photos",
      "/lexique",
      // "/wado-ryu",
      // "/historique",
      // "/kata",
      // "/arbitrage-karate",
      "/techniques-krav-maga",
      // "/vote-sondage-utilisateur",
      "/cours-agenda",
      "/listing-adherents",
      "/appel-presence",
      "/tableau-appel",
      "/passage-ceinture",
      "/grade-cn",
      // "/suivi-competitions",
      // "/palmares",
      "/todolist",
      "/techniques-enseignants",
      "/aide-enseignants",
    ],
    admin: [
      "/tableau-de-bord",
      "/dossier-d-inscription",
      "/fiche-adherent",
      // "/absences",
      "/messages",
      "/chat",
      "/boutique",
      "/commandes-&-reglements",
      "/actualites",
      "/galerie-photos",
      "/lexique",
      // "/wado-ryu",
      // "/historique",
      // "/kata",
      // "/arbitrage-karate",
      "/techniques-krav-maga",
      // "/vote-sondage-utilisateur",
      "/calendrier",
      "/listing-adherents",
      "/appel-presence",
      "/tableau-appel",
      "/passage-ceinture",
      "/grade-cn",
      // "/suivi-competitions",
      "/evenements",
      // "/palmares",
      "/todolist",
      "/techniques-enseignants",
      "/dashboard-adherents",
      "/dashboard-comptabilite",
      "/details-adherent",
      "/listing-utilisateurs",
      "/listing-adherents-admin",
      "/reglements-inscriptions",
      "/dossiers-d-inscription",
      "/paiements",
      "/articles-admin",
      "/evenements",
      "/cours-agenda",
      "/depenses-recettes",
      "/coachings",
      "/heures-enseignement",
      "/inventaire",
      "/chat-page-admin",
      "/message-admin",
      // "/vote-sondage-admin",
      "/parametres",
      "/requetes-admin",
      "/progression-etape",
      "/abonnement",
      "/update",
      "/aide-conseils",
    ],
  };

  const roleKey = toRoleKey(role);

  useEffect(() => {
    if (!loaded) return; // attend le chargement des settings
    const currentNorm = normalizePath(location.pathname);

    // base (raw pour navigate, norm pour comparaisons)
    const base = (paths[roleKey] || []).map((raw) => ({
      raw: raw.replace(/\/$/, ""),
      norm: normalizePath(raw),
    }));

    // Filtre final :
    // - si la route a une clé display_* → on inclut UNIQUEMENT si store[key] est true/1/"1"
    // - si pas de clé → on inclut
    const filtered = base.filter(({ norm }) => {
      const key = DISPLAY_BY_PATH[norm];
      if (!key) return true; // pas de display → visible
      return isTrue(store[key]); // display → visible seulement si true
    });

    setCurrentPaths(filtered.map((x) => x.raw));

    const idx = filtered.findIndex((x) => x.norm === currentNorm);
    setCurrentIndex(idx);
  }, [loaded, location.pathname, roleKey, store]);

  const hasPrevious = currentIndex > 0;
  const hasNext = currentIndex < currentPaths.length - 1;
  const displayIndex = currentIndex === -1 ? 0 : currentIndex + 1;

  return (
    <div className="flex items-center justify-between p-4 dark:bg-slate-800 bg-slate-200 mb-4 font-montserrat">
      {hasPrevious ? (
        <button
          onClick={() => navigate(currentPaths[currentIndex - 1])}
          className="text-primary hover:text-opacity-80 dark:text-white"
        >
          <FaArrowLeft size={24} />
        </button>
      ) : (
        <div className="w-6" />
      )}

      <div className="flex-grow text-center text-gray-800 dark:text-white">
        {displayIndex} / {currentPaths.length}
      </div>

      {hasNext ? (
        <button
          onClick={() => navigate(currentPaths[currentIndex + 1])}
          className="text-primary hover:text-opacity-80 dark:text-white"
        >
          <FaArrowRight size={24} />
        </button>
      ) : (
        <div className="w-6" />
      )}
    </div>
  );
};

export default DashboardNavigation;
